import { useRef, useEffect, useState } from 'react'
import { flexRender, type Table as TanStackTable } from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { DataTableEmpty } from './DataTableEmpty'
import { DataTableLoader } from './DataTableLoader'
import { DataTablePagination } from './DataTablePagination'
import { SortIcon } from './SortIcon'

export interface DataTableProps<TData> {
  /** Instancia de tabla de TanStack Table */
  table: TanStackTable<TData>
  /** Mostrar estado de carga */
  isLoading?: boolean
  /** Mensaje cuando no hay datos */
  emptyMessage?: string
  /** Icono para estado vacío */
  emptyIcon?: React.ReactNode
  /** Acción para estado vacío (ej: botón crear) */
  emptyAction?: React.ReactNode
  /** Headers sticky */
  stickyHeader?: boolean
  /** Mostrar paginación */
  showPagination?: boolean
  /** Mostrar líneas verticales entre columnas */
  withVerticalDividers?: boolean
  /** Clase CSS adicional */
  className?: string
  /** ID de columna(s) sticky a la derecha */
  stickyRightColumns?: string[]
  /** Habilitar redimensionamiento de columnas */
  enableColumnResizing?: boolean
  /** Habilitar scroll interno (la tabla crece y usa el espacio disponible) */
  enableInternalScroll?: boolean
  /** Habilitar scroll vertical (el contenedor tiene altura fija y solo scrollea el body) */
  enableVerticalScroll?: boolean
  /** Contenido custom para el footer de la tabla (sticky) */
  footerContent?: React.ReactNode
  /** Callback al hacer click en una fila */
  onRowClick?: (row: TData) => void
}

/**
 * Componente DataTable reutilizable basado en TanStack Table
 *
 * @example
 * const table = useReactTable({
 *   data: servicios,
 *   columns,
 *   getCoreRowModel: getCoreRowModel(),
 *   getSortedRowModel: getSortedRowModel(),
 * })
 *
 * return <DataTable table={table} isLoading={isLoading} />
 */
export function DataTable<TData>({
  table,
  isLoading = false,
  emptyMessage = 'No hay datos para mostrar',
  emptyIcon,
  emptyAction,
  stickyHeader = true,
  showPagination = false,
  withVerticalDividers = false,
  className,
  stickyRightColumns = [],
  enableColumnResizing = false,
  enableInternalScroll = false,
  enableVerticalScroll = false,
  footerContent,
  onRowClick,
}: DataTableProps<TData>) {
  // Clase para líneas verticales entre columnas
  const dividerClass = withVerticalDividers ? 'border-r border-border/50 last:border-r-0' : ''

  // ORR-327: un click sobre un control propio de la celda (botón de copiar, trigger de
  // popover, checkbox, link) no debe además disparar la acción de la fila. Varias celdas ya
  // cortan la propagación a mano; esto cubre a las que no, en toda tabla con onRowClick.
  const handleRowClick = onRowClick
    ? (row: TData) => (e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement
        if (target.closest('button, a, input, select, textarea, [role="menuitem"]')) return
        onRowClick(row)
      }
    : undefined

  // Helper para determinar si una columna es sticky
  const isStickyRight = (columnId: string) => stickyRightColumns.includes(columnId)

  // Clases CSS para columnas sticky a la derecha
  const getStickyRightClasses = (columnId: string) => {
    if (!isStickyRight(columnId)) return ''
    return 'sticky right-0 bg-background shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]'
  }

  // Verificar si la tabla tiene resize habilitado
  const isResizingEnabled = enableColumnResizing && table.options.enableColumnResizing

  // Calcular el ancho total de la tabla para column resizing
  const tableWidth = isResizingEnabled ? table.getTotalSize() : undefined

  // Refs y estado para scroll sincronizado horizontal.
  // DEBEN declararse antes de cualquier early return: si se mueven después,
  // los renders con isLoading o tabla vacía saltean estos hooks y el siguiente
  // render rompe el orden de hooks (Rules of Hooks).
  const headerScrollRef = useRef<HTMLDivElement>(null)
  const bodyScrollRef = useRef<HTMLDivElement>(null)
  const [showLeftShadow, setShowLeftShadow] = useState(false)
  const [showRightShadow, setShowRightShadow] = useState(false)

  // Sincronizar scroll horizontal entre header y body
  useEffect(() => {
    if (!enableInternalScroll) return

    const headerScroll = headerScrollRef.current
    const bodyScroll = bodyScrollRef.current

    if (!headerScroll || !bodyScroll) return

    const syncHeaderScroll = () => {
      if (bodyScroll) {
        headerScroll.scrollLeft = bodyScroll.scrollLeft
      }
    }

    const syncBodyScroll = () => {
      if (headerScroll) {
        bodyScroll.scrollLeft = headerScroll.scrollLeft
      }
    }

    // Detectar si hay scroll disponible y actualizar sombras
    const updateShadows = () => {
      const scrollLeft = bodyScroll.scrollLeft
      const maxScroll = bodyScroll.scrollWidth - bodyScroll.clientWidth

      setShowLeftShadow(scrollLeft > 10)
      setShowRightShadow(scrollLeft < maxScroll - 10)
    }

    // Ejecutar al montar y cuando cambie el tamaño
    updateShadows()
    const resizeObserver = new ResizeObserver(updateShadows)
    resizeObserver.observe(bodyScroll)

    bodyScroll.addEventListener('scroll', syncHeaderScroll)
    headerScroll.addEventListener('scroll', syncBodyScroll)
    bodyScroll.addEventListener('scroll', updateShadows)

    return () => {
      bodyScroll.removeEventListener('scroll', syncHeaderScroll)
      headerScroll.removeEventListener('scroll', syncBodyScroll)
      bodyScroll.removeEventListener('scroll', updateShadows)
      resizeObserver.disconnect()
    }
  }, [enableInternalScroll])

  // Estado de carga
  if (isLoading) {
    return <DataTableLoader columnCount={table.getAllColumns().length} />
  }

  // Estado vacío
  if (table.getRowModel().rows.length === 0) {
    return (
      <DataTableEmpty message={emptyMessage} icon={emptyIcon} action={emptyAction} />
    )
  }

  // Renderizado con doble contenedor cuando enableInternalScroll está activo
  if (enableInternalScroll) {
    return (
      <div className={cn('h-full flex flex-col space-y-0', className)}>
        <div className="flex flex-col border rounded-md overflow-hidden flex-1 min-h-0 relative">
          {/* Header fijo con scroll horizontal */}
          <div
            ref={headerScrollRef}
            className="overflow-x-auto overflow-y-hidden border-b bg-background [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <Table
              style={
                isResizingEnabled
                  ? { width: tableWidth, minWidth: '100%', tableLayout: 'fixed' }
                  : undefined
              }
            >
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const canSort = header.column.getCanSort()
                      const sortDirection = header.column.getIsSorted()
                      const canResize = isResizingEnabled && header.column.getCanResize()

                      return (
                        <TableHead
                          key={header.id}
                          className={cn(
                            'relative',
                            isResizingEnabled && 'overflow-hidden',
                            canSort && !canResize && 'cursor-pointer select-none hover:bg-muted/50',
                            dividerClass,
                            getStickyRightClasses(header.id),
                            header.column.columnDef.meta?.headerClassName
                          )}
                          style={{
                            width: header.getSize(),
                            minWidth: header.column.columnDef.minSize,
                            maxWidth: header.column.columnDef.maxSize,
                          }}
                          onClick={
                            canSort && !canResize && !header.column.getIsResizing()
                              ? header.column.getToggleSortingHandler()
                              : undefined
                          }
                        >
                          <div
                            className={cn(
                              'flex items-center gap-2',
                              canSort && canResize && 'cursor-pointer select-none hover:bg-muted/50 rounded px-1 -mx-1'
                            )}
                            onClick={
                              canSort && canResize && !header.column.getIsResizing()
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            {canSort && <SortIcon direction={sortDirection} />}
                          </div>
                          {canResize && (
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              onDoubleClick={() => header.column.resetSize()}
                              className={cn(
                                'absolute right-0 top-0 h-full w-4 cursor-col-resize select-none touch-none group',
                                'flex items-center justify-end'
                              )}
                              title="Arrastra para redimensionar. Doble clic para restablecer."
                            >
                              <div
                                className={cn(
                                  'h-full w-[3px] rounded-full transition-colors',
                                  'bg-transparent group-hover:bg-primary/50 group-active:bg-primary',
                                  header.column.getIsResizing() && 'bg-primary'
                                )}
                              />
                            </div>
                          )}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
            </Table>
          </div>

          {/* Body con scroll vertical y horizontal */}
          <div
            ref={bodyScrollRef}
            className="flex-1 overflow-auto [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:bg-muted [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'hsl(var(--muted)) transparent' }}
          >
            <Table
              style={
                isResizingEnabled
                  ? { width: tableWidth, minWidth: '100%', tableLayout: 'fixed' }
                  : undefined
              }
            >
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() ? 'selected' : undefined}
                    onClick={handleRowClick?.(row.original)}
                    className={onRowClick ? 'cursor-pointer' : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          isResizingEnabled && 'overflow-hidden',
                          dividerClass,
                          getStickyRightClasses(cell.column.id),
                          cell.column.columnDef.meta?.cellClassName
                        )}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.columnDef.minSize,
                          maxWidth: cell.column.columnDef.maxSize,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
              {footerContent && (
                <tfoot className="sticky bottom-0 z-10 bg-muted/80 backdrop-blur-sm border-t">
                  {footerContent}
                </tfoot>
              )}
            </Table>
          </div>

          {/* Indicador de scroll izquierdo */}
          {showLeftShadow && (
            <div
              className="absolute left-0 top-0 bottom-0 w-16 pointer-events-none z-10"
              style={{
                background: 'linear-gradient(to right, hsl(var(--background)) 0%, hsl(var(--background) / 0.8) 40%, transparent 100%)',
                boxShadow: 'inset 6px 0 12px -6px rgba(0, 0, 0, 0.15)',
              }}
              aria-hidden="true"
            />
          )}

          {/* Indicador de scroll derecho */}
          {showRightShadow && (
            <div
              className="absolute right-0 top-0 bottom-0 w-16 pointer-events-none z-10"
              style={{
                background: 'linear-gradient(to left, hsl(var(--background)) 0%, hsl(var(--background) / 0.8) 40%, transparent 100%)',
                boxShadow: 'inset -6px 0 12px -6px rgba(0, 0, 0, 0.15)',
              }}
              aria-hidden="true"
            />
          )}
        </div>

        {showPagination && <DataTablePagination table={table} />}
      </div>
    )
  }

  // Renderizado con scroll vertical (altura fija, solo scrollea el body)
  if (enableVerticalScroll) {
    return (
      <div className={cn('h-full flex flex-col gap-4', className)}>
        <div className="flex-1 min-h-0 rounded-md border overflow-hidden">
          <div className="h-full overflow-auto">
            <Table
              wrapperClassName="relative w-full"
              style={
                isResizingEnabled
                  ? { width: tableWidth, minWidth: '100%', tableLayout: 'fixed' }
                  : undefined
              }
            >
              <TableHeader className={cn(stickyHeader && 'sticky top-0 z-10 bg-background')}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const canSort = header.column.getCanSort()
                      const sortDirection = header.column.getIsSorted()
                      const canResize = isResizingEnabled && header.column.getCanResize()

                      return (
                        <TableHead
                          key={header.id}
                          className={cn(
                            'relative',
                            isResizingEnabled && 'overflow-hidden',
                            canSort && !canResize && 'cursor-pointer select-none hover:bg-muted/50',
                            dividerClass,
                            getStickyRightClasses(header.id),
                            header.column.columnDef.meta?.headerClassName
                          )}
                          style={{
                            width: header.getSize(),
                            minWidth: header.column.columnDef.minSize,
                            maxWidth: header.column.columnDef.maxSize,
                          }}
                          onClick={
                            canSort && !canResize && !header.column.getIsResizing()
                              ? header.column.getToggleSortingHandler()
                              : undefined
                          }
                        >
                          <div
                            className={cn(
                              'flex items-center gap-2',
                              canSort && canResize && 'cursor-pointer select-none hover:bg-muted/50 rounded px-1 -mx-1'
                            )}
                            onClick={
                              canSort && canResize && !header.column.getIsResizing()
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            {canSort && <SortIcon direction={sortDirection} />}
                          </div>
                          {canResize && (
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              onDoubleClick={() => header.column.resetSize()}
                              className={cn(
                                'absolute right-0 top-0 h-full w-4 cursor-col-resize select-none touch-none group',
                                'flex items-center justify-end'
                              )}
                              title="Arrastra para redimensionar. Doble clic para restablecer."
                            >
                              <div
                                className={cn(
                                  'h-full w-[3px] rounded-full transition-colors',
                                  'bg-transparent group-hover:bg-primary/50 group-active:bg-primary',
                                  header.column.getIsResizing() && 'bg-primary'
                                )}
                              />
                            </div>
                          )}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() ? 'selected' : undefined}
                    onClick={handleRowClick?.(row.original)}
                    className={onRowClick ? 'cursor-pointer' : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          isResizingEnabled && 'overflow-hidden',
                          dividerClass,
                          getStickyRightClasses(cell.column.id),
                          cell.column.columnDef.meta?.cellClassName
                        )}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.columnDef.minSize,
                          maxWidth: cell.column.columnDef.maxSize,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
              {footerContent && (
                <tfoot className="sticky bottom-0 z-10 bg-muted/80 backdrop-blur-sm border-t">
                  {footerContent}
                </tfoot>
              )}
            </Table>
          </div>
        </div>

        {showPagination && (
          <div className="shrink-0">
            <DataTablePagination table={table} />
          </div>
        )}
      </div>
    )
  }

  // Renderizado original para tablas sin scroll interno
  return (
    <div className={cn('space-y-4', className)}>
      <div className="rounded-md border overflow-x-auto">
        <Table
          style={
            isResizingEnabled
              ? { width: tableWidth, minWidth: '100%', tableLayout: 'fixed' }
              : undefined
          }
        >
          <TableHeader
            className={cn(stickyHeader && 'sticky top-0 z-10 bg-background')}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const sortDirection = header.column.getIsSorted()

                  const canResize = isResizingEnabled && header.column.getCanResize()

                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'relative',
                        isResizingEnabled && 'overflow-hidden',
                        canSort && !canResize && 'cursor-pointer select-none hover:bg-muted/50',
                        dividerClass,
                        getStickyRightClasses(header.id),
                        header.column.columnDef.meta?.headerClassName
                      )}
                      style={{
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize,
                        maxWidth: header.column.columnDef.maxSize,
                      }}
                      onClick={
                        canSort && !canResize && !header.column.getIsResizing()
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      <div
                        className={cn(
                          'flex items-center gap-2',
                          canSort && canResize && 'cursor-pointer select-none hover:bg-muted/50 rounded px-1 -mx-1'
                        )}
                        onClick={
                          canSort && canResize && !header.column.getIsResizing()
                            ? header.column.getToggleSortingHandler()
                            : undefined
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {canSort && <SortIcon direction={sortDirection} />}
                      </div>
                      {/* Resize Handle */}
                      {canResize && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onDoubleClick={() => header.column.resetSize()}
                          className={cn(
                            'absolute right-0 top-0 h-full w-4 cursor-col-resize select-none touch-none group',
                            'flex items-center justify-end'
                          )}
                          title="Arrastra para redimensionar. Doble clic para restablecer."
                        >
                          {/* Visual indicator (thin line) - alineada al borde derecho */}
                          <div
                            className={cn(
                              'h-full w-[3px] rounded-full transition-colors',
                              'bg-transparent group-hover:bg-primary/50 group-active:bg-primary',
                              header.column.getIsResizing() && 'bg-primary'
                            )}
                          />
                        </div>
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() ? 'selected' : undefined}
                onClick={handleRowClick?.(row.original)}
                className={onRowClick ? 'cursor-pointer' : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      isResizingEnabled && 'overflow-hidden',
                      dividerClass,
                      getStickyRightClasses(cell.column.id),
                      cell.column.columnDef.meta?.cellClassName
                    )}
                    style={{
                      width: cell.column.getSize(),
                      minWidth: cell.column.columnDef.minSize,
                      maxWidth: cell.column.columnDef.maxSize,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          {footerContent && (
            <tfoot className="sticky bottom-0 z-10 bg-muted/80 backdrop-blur-sm border-t">
              {footerContent}
            </tfoot>
          )}
        </Table>
      </div>

      {showPagination && <DataTablePagination table={table} />}
    </div>
  )
}
